import type {Language, LocalizedString} from '@cohort/shared/schema/common';
import {z} from 'zod';

export const GarminConnectTriggerIntegrationActivityGroups = [
  'RUNNING',
  'CYCLING',
  'WALKING',
  'SWIMMING',
] as const;
export type GarminConnectTriggerIntegrationActivityGroupsType =
  (typeof GarminConnectTriggerIntegrationActivityGroups)[number];

export const GarminConnectTriggerIntegrationActivityTypes = [
  'RUNNING',
  'INDOOR_RUNNING',
  'OBSTACLE_RUN',
  'STREET_RUNNING',
  'TRACK_RUNNING',
  'TRAIL_RUNNING',
  'TREADMILL_RUNNING',
  'ULTRA_RUN',
  'VIRTUAL_RUN',
  'CYCLING',
  'BMX',
  'CYCLOCROSS',
  'DOWNHILL_BIKING',
  'E_BIKE_FITNESS',
  'E_BIKE_MOUNTAIN',
  'GRAVEL_CYCLING',
  'HANDCYCLING',
  'INDOOR_CYCLING',
  'INDOOR_HANDCYCLING',
  'MOUNTAIN_BIKING',
  'RECUMBENT_CYCLING',
  'ROAD_BIKING',
  'TRACK_CYCLING',
  'VIRTUAL_RIDE',
  'FITNESS_EQUIPMENT',
  'BOULDERING',
  'ELLIPTICAL',
  'HIIT',
  'INDOOR_CARDIO',
  'INDOOR_CLIMBING',
  'INDOOR_ROWING',
  'ONSHORE_GRINDING',
  'PILATES',
  'STAIR_CLIMBING',
  'STRENGTH_TRAINING',
  'YOGA',
  'SWIMMING',
  'LAP_SWIMMING',
  'OPEN_WATER_SWIMMING',
  'WALKING',
  'CASUAL_WALKING',
  'SPEED_WALKING',
  'HIKING',
  'WINTER_SPORTS',
  'BACKCOUNTRY_SKIING',
  'BACKCOUNTRY_SNOWBOARDING',
  'CROSS_COUNTRY_SKIING_WS',
  'RESORT_SKIING_SNOWBOARDING_WS',
  'SKATE_SKIING_WS',
  'SKATING_WS',
  'SNOW_SHOE_WS',
  'SNOWMOBILING_WS',
  'WATER_SPORTS',
  'BOATING',
  'FISHING',
  'KAYAKING',
  'KITEBOARDING',
  'OFFSHORE_GRINDING',
  'ROWING',
  'SAILING',
  'SNORKELING',
  'STAND_UP_PADDLEBOARDING',
  'SURFING',
  'WAKEBOARDING',
  'WATERSKIING',
  'WHITEWATER_RAFTING',
  'WINDSURFING',
  'TEAM_SPORTS',
  'AMERICAN_FOOTBALL',
  'BASEBALL',
  'BASKETBALL',
  'CRICKET',
  'FIELD_HOCKEY',
  'ICE_HOCKEY',
  'LACROSSE',
  'RUGBY',
  'SOCCER',
  'SOFTBALL',
  'ULTIMATE_DISC',
  'VOLLEYBALL',
  'RACKET_SPORTS',
  'BADMINTON',
  'PADEL',
  'PICKLEBALL',
  'PLATFORM_TENNIS',
  'RACQUETBALL',
  'SQUASH',
  'TABLE_TENNIS',
  'TENNIS',
  'PARA_SPORTS',
  'WHEELCHAIR_PUSH_RUN',
  'WHEELCHAIR_PUSH_WALK',
  'OTHER',
  'BOXING',
  'BREATHWORK',
  'DISC_GOLF',
  'FLOOR_CLIMBING',
  'GOLF',
  'INLINE_SKATING',
  'MIXED_MARTIAL_ARTS',
  'MOUNTAINEERING',
  'ROCK_CLIMBING',
  'STOP_WATCH',

  // NOT IN DOCS
  'DIVING',
  'APNEA',
  'APNEA_HUNTING',
  'CGR_DIVING',
  'GAUGE_DIVING',
  'MULTI_GAS_DIVING',
  'SINGLE_GAS_DIVING',
  'CANOEING',
  'WAKESURFING',
  'TUBING',
  'ARCHERY',
  'ATV',
  'AUTO_RACING',
  'DRIVING',
  'GAMING',
  'FLYING',
  'HAND_GLIDING',
  'HORSEBACK_RIDING',
  'HUNTING',
  'MEDITATING',
  'MOTOCROSS',
  'MOTORCYCLING',
  'OVERLAND',
  'DRONE',
  'SKY_DIVING',
  'WINGSUIT_FLYING',
] as const;
export type GarminConnectTriggerIntegrationActivityTypes =
  (typeof GarminConnectTriggerIntegrationActivityTypes)[number];

export const GarminConnectTriggerIntegrationActivityTypesMappedByGroups: Record<
  GarminConnectTriggerIntegrationActivityGroupsType,
  GarminConnectTriggerIntegrationActivityTypes[]
> = {
  RUNNING: [
    'RUNNING',
    'INDOOR_RUNNING',
    'OBSTACLE_RUN',
    'STREET_RUNNING',
    'TRACK_RUNNING',
    'TRAIL_RUNNING',
    'TREADMILL_RUNNING',
    'ULTRA_RUN',
    'VIRTUAL_RUN',
    'WHEELCHAIR_PUSH_RUN',
  ],
  CYCLING: [
    'CYCLING',
    'BMX',
    'CYCLOCROSS',
    'DOWNHILL_BIKING',
    'E_BIKE_FITNESS',
    'E_BIKE_MOUNTAIN',
    'GRAVEL_CYCLING',
    'HANDCYCLING',
    'INDOOR_CYCLING',
    'INDOOR_HANDCYCLING',
    'MOUNTAIN_BIKING',
    'RECUMBENT_CYCLING',
    'ROAD_BIKING',
    'TRACK_CYCLING',
    'VIRTUAL_RIDE',
  ],
  WALKING: ['WALKING', 'CASUAL_WALKING', 'SPEED_WALKING', 'HIKING', 'WHEELCHAIR_PUSH_WALK'],
  SWIMMING: ['SWIMMING', 'LAP_SWIMMING', 'OPEN_WATER_SWIMMING'],
} as const;

export const GarminConnectTriggerIntegrationAvailableActivityGroupsSchema = z.enum(
  GarminConnectTriggerIntegrationActivityGroups
);
export const GarminConnectTriggerIntegrationAvailableActivityTypesSchema = z.enum(
  GarminConnectTriggerIntegrationActivityTypes
);

export const GarminConnectTriggerIntegrationActivityGroupsTranslation: Record<
  GarminConnectTriggerIntegrationActivityGroupsType,
  LocalizedString
> = {
  RUNNING: {en: 'Running', fr: 'Course à pied', es: 'Correr', de: 'Laufen'},
  CYCLING: {en: 'Cycling', fr: 'Cyclisme', es: 'Ciclismo', de: 'Radfahren'},
  WALKING: {en: 'Walking', fr: 'Marche à pied', es: 'Caminata', de: 'Gehen'},
  SWIMMING: {en: 'Swimming', fr: 'Natation', es: 'Natación', de: 'Schwimmen'},
};

export const GarminConnectTriggerIntegrationActivityTypesTranslation: Record<
  GarminConnectTriggerIntegrationActivityTypes,
  LocalizedString
> = {
  RUNNING: {en: 'Running', fr: 'Course à pied', es: 'Correr', de: 'Laufen'},
  INDOOR_RUNNING: {
    en: 'Indoor Running',
    fr: 'Course en salle',
    es: 'Correr en interiores',
    de: 'Indoor-Laufen',
  },
  OBSTACLE_RUN: {
    en: 'Obstacle Course Racing',
    fr: "Course d'obstacles",
    es: 'Carrera de obstáculos',
    de: 'Hindernislauf',
  },
  STREET_RUNNING: {
    en: 'Street Running',
    fr: 'Course à pied urbaine',
    es: 'Correr en la calle',
    de: 'Straßenlauf',
  },
  TRACK_RUNNING: {
    en: 'Track Running',
    fr: 'Course à pied sur piste',
    es: 'Correr en pista',
    de: 'Bahnlauf',
  },
  TRAIL_RUNNING: {en: 'Trail Running', fr: 'Trail', es: 'Correr en sendero', de: 'Trailrunning'},
  TREADMILL_RUNNING: {
    en: 'Treadmill Running',
    fr: 'Course à pied sur tapis roulant',
    es: 'Correr en cinta de correr',
    de: 'Laufband',
  },
  ULTRA_RUN: {en: 'Ultra Running', fr: 'Ultrafond', es: 'Ultra Running', de: 'Ultralauf'},
  VIRTUAL_RUN: {
    en: 'Virtual Running',
    fr: 'Course virtuelle',
    es: 'Correr virtualmente',
    de: 'Virtuelles Laufen',
  },
  CYCLING: {en: 'Cycling', fr: 'Cyclisme', es: 'Ciclismo', de: 'Radfahren'},
  BMX: {en: 'BMX', fr: 'BMX', es: 'BMX', de: 'BMX'},
  CYCLOCROSS: {en: 'Cyclocross', fr: 'Cyclocross', es: 'Ciclocross', de: 'Cyclocross'},
  DOWNHILL_BIKING: {
    en: 'Downhill Biking',
    fr: 'Cyclisme de descente',
    es: 'Bicicleta de descenso',
    de: 'Downhill-Biken',
  },
  E_BIKE_FITNESS: {
    en: 'eBiking',
    fr: 'Vélo électrique',
    es: 'Bicicleta eléctrica',
    de: 'E-Bike-Fahren',
  },
  E_BIKE_MOUNTAIN: {
    en: 'eMountain Biking',
    fr: 'VTT électrique',
    es: 'Bicicleta de montaña eléctrica',
    de: 'E-Mountainbiken',
  },
  GRAVEL_CYCLING: {
    en: 'Gravel/Unpaved Cycling',
    fr: 'Gravel bike',
    es: 'Ciclismo en gravilla/sin pavimentar',
    de: 'Gravel-/Schotterradfahren',
  },
  HANDCYCLING: {en: 'Handcycling', fr: 'Cyclisme à main', es: 'Handcycling', de: 'Handbiken'},
  INDOOR_CYCLING: {
    en: 'Indoor Cycling',
    fr: "Vélo d'intérieur",
    es: 'Ciclismo en interiores',
    de: 'Indoor-Radfahren',
  },
  INDOOR_HANDCYCLING: {
    en: 'Indoor Handcycling',
    fr: "Cyclisme à main d'intérieur",
    es: 'Handcycling en interiores',
    de: 'Indoor-Handbiken',
  },
  MOUNTAIN_BIKING: {
    en: 'Mountain Biking',
    fr: 'VTT',
    es: 'Bicicleta de montaña',
    de: 'Mountainbiken',
  },
  RECUMBENT_CYCLING: {
    en: 'Recumbent Cycling',
    fr: 'Vélo couché',
    es: 'Ciclismo reclinado',
    de: 'Liegeradfahren',
  },
  ROAD_BIKING: {
    en: 'Road Cycling',
    fr: 'Cyclisme sur route',
    es: 'Ciclismo en carretera',
    de: 'Straßenradfahren',
  },
  TRACK_CYCLING: {
    en: 'Track Cycling',
    fr: 'Cyclisme sur piste',
    es: 'Ciclismo en pista',
    de: 'Bahnradfahren',
  },
  VIRTUAL_RIDE: {
    en: 'Virtual Cycling',
    fr: 'Cyclisme virtuel',
    es: 'Ciclismo virtual',
    de: 'Virtuelles Radfahren',
  },
  FITNESS_EQUIPMENT: {
    en: 'Gym & Fitness Equipment',
    fr: 'Appareil de musculation et de fitness',
    es: 'Equipo de gimnasio y fitness',
    de: 'Fitnessgeräte',
  },
  BOULDERING: {en: 'Bouldering', fr: 'Escalade sur bloc', es: 'Escalada en bloque', de: 'Bouldern'},
  ELLIPTICAL: {en: 'Elliptical', fr: 'Vélo elliptique', es: 'Elíptica', de: 'Crosstrainer'},
  HIIT: {en: 'HIIT', fr: 'HIIT', es: 'HIIT', de: 'HIIT'},
  INDOOR_CARDIO: {en: 'Cardio', fr: 'Cardio', es: 'Cardio en interiores', de: 'Cardio'},
  INDOOR_CLIMBING: {
    en: 'Indoor Climbing',
    fr: 'Escalade en salle',
    es: 'Escalada en interiores',
    de: 'Indoor-Klettern',
  },
  INDOOR_ROWING: {
    en: 'Indoor Rowing',
    fr: "Rameur d'intérieur",
    es: 'Remo en interiores',
    de: 'Indoor-Rudern',
  },
  ONSHORE_GRINDING: {
    en: 'Onshore Grinding',
    fr: 'Grinding en navigation',
    es: 'Molienda en tierra',
    de: 'Grinding an Land',
  },
  PILATES: {en: 'Pilates', fr: 'Pilates', es: 'Pilates', de: 'Pilates'},
  STAIR_CLIMBING: {en: 'Stair Stepper', fr: 'Stepper', es: 'Escaladora', de: 'Stepper'},
  STRENGTH_TRAINING: {
    en: 'Strength Training',
    fr: 'Musculation',
    es: 'Entrenamiento de fuerza',
    de: 'Krafttraining',
  },
  YOGA: {en: 'Yoga', fr: 'Yoga', es: 'Yoga', de: 'Yoga'},
  SWIMMING: {en: 'Swimming', fr: 'Natation', es: 'Natación', de: 'Schwimmen'},
  LAP_SWIMMING: {
    en: 'Pool Swimming',
    fr: 'En piscine',
    es: 'Natación en piscina',
    de: 'Bahnenschwimmen',
  },
  OPEN_WATER_SWIMMING: {
    en: 'Open Water Swimming',
    fr: 'Natation en eau libre',
    es: 'Natación en aguas abiertas',
    de: 'Freiwasserschwimmen',
  },
  WALKING: {en: 'Walking', fr: 'Marche à pied', es: 'Caminata', de: 'Gehen'},
  CASUAL_WALKING: {en: 'Casual Walking', fr: 'Marche', es: 'Caminata casual', de: 'Spazierengehen'},
  SPEED_WALKING: {
    en: 'Speed Walking',
    fr: 'Marche rapide',
    es: 'Caminata rápida',
    de: 'Speedwalking',
  },
  HIKING: {en: 'Hiking', fr: 'Randonnée', es: 'Senderismo', de: 'Wandern'},
  WINTER_SPORTS: {
    en: 'Winter Sports',
    fr: "Sports d'hiver",
    es: 'Deportes de invierno',
    de: 'Wintersport',
  },
  BACKCOUNTRY_SKIING: {
    en: 'Backcountry Skiing',
    fr: 'Ski de randonnée',
    es: 'Esquí de travesía',
    de: 'Tourenskilauf',
  },
  BACKCOUNTRY_SNOWBOARDING: {
    en: 'Backcountry Snowboarding',
    fr: 'Snowboard backcountry',
    es: 'Snowboard de travesía',
    de: 'Backcountry-Snowboarden',
  },
  CROSS_COUNTRY_SKIING_WS: {
    en: 'Cross Country Classic Skiing',
    fr: 'Ski de fond classique',
    es: 'Esquí de fondo clásico',
    de: 'Klassischer Skilanglauf',
  },
  RESORT_SKIING_SNOWBOARDING_WS: {
    en: 'Resort Skiing/Snowboarding',
    fr: 'Ski en station',
    es: 'Esquí/Snowboard en estación',
    de: 'Pisten-Ski/Snowboard',
  },
  SKATE_SKIING_WS: {
    en: 'Cross Country Skate Skiing',
    fr: 'Ski de fond skating',
    es: 'Esquí de fondo estilo patinaje',
    de: 'Skating-Skilanglauf',
  },
  SKATING_WS: {
    en: 'Skating',
    fr: 'Patin à glace/à roulettes',
    es: 'Patinaje',
    de: 'Eislaufen/Rollschuhlaufen',
  },
  SNOW_SHOE_WS: {
    en: 'Snowshoeing',
    fr: 'Raquette à neige',
    es: 'Senderismo en nieve',
    de: 'Schneeschuhwandern',
  },
  SNOWMOBILING_WS: {
    en: 'Snowmobiling',
    fr: 'Motoneige',
    es: 'Motos de nieve',
    de: 'Schneemobilfahren',
  },
  WATER_SPORTS: {
    en: 'Water Sports',
    fr: 'Sports nautiques',
    es: 'Deportes acuáticos',
    de: 'Wassersport',
  },
  BOATING: {en: 'Boating', fr: 'Bateau', es: 'Navegación', de: 'Bootfahren'},
  FISHING: {en: 'Fishing', fr: 'Pêche', es: 'Pesca', de: 'Angeln'},
  KAYAKING: {en: 'Kayaking', fr: 'Canoë kayak', es: 'Kayak', de: 'Kajakfahren'},
  KITEBOARDING: {en: 'Kiteboarding', fr: 'Kiteboard', es: 'Kiteboarding', de: 'Kiteboarding'},
  OFFSHORE_GRINDING: {
    en: 'Offshore Grinding',
    fr: 'Entraînement au Grinding',
    es: 'Entrenamiento en alta mar',
    de: 'Grinding auf See',
  },
  ROWING: {en: 'Rowing', fr: 'Aviron', es: 'Remo', de: 'Rudern'},
  SAILING: {en: 'Sailing', fr: 'Navigation à la voile', es: 'Vela', de: 'Segeln'},
  SNORKELING: {en: 'Snorkeling', fr: 'Plongée avec tuba', es: 'Buceo con tubo', de: 'Schnorcheln'},
  STAND_UP_PADDLEBOARDING: {
    en: 'Stand Up Paddleboarding',
    fr: 'Stand up paddle',
    es: 'Paddleboarding de pie',
    de: 'Stand-up-Paddling',
  },
  SURFING: {en: 'Surfing', fr: 'Surf', es: 'Surf', de: 'Surfen'},
  WAKEBOARDING: {en: 'Wakeboarding', fr: 'Wakeboard', es: 'Wakeboarding', de: 'Wakeboarding'},
  WATERSKIING: {en: 'Waterskiing', fr: 'Ski nautique', es: 'Esquí acuático', de: 'Wasserski'},
  WHITEWATER_RAFTING: {
    en: 'Whitewater',
    fr: 'Eaux vives',
    es: 'Rafting en aguas bravas',
    de: 'Wildwasser',
  },
  WINDSURFING: {en: 'Windsurfing', fr: 'Planche à voile', es: 'Windsurf', de: 'Windsurfen'},
  TEAM_SPORTS: {
    en: 'Team Sports',
    fr: 'Sports collectifs',
    es: 'Deportes de equipo',
    de: 'Mannschaftssport',
  },
  AMERICAN_FOOTBALL: {
    en: 'American Football',
    fr: 'Football américain',
    es: 'Fútbol americano',
    de: 'American Football',
  },
  BASEBALL: {en: 'Baseball', fr: 'Baseball', es: 'Béisbol', de: 'Baseball'},
  BASKETBALL: {en: 'Basketball', fr: 'Basket-ball', es: 'Baloncesto', de: 'Basketball'},
  CRICKET: {en: 'Cricket', fr: 'Cricket', es: 'Críquet', de: 'Cricket'},
  FIELD_HOCKEY: {
    en: 'Field Hockey',
    fr: 'Hockey sur gazon',
    es: 'Hockey sobre césped',
    de: 'Feldhockey',
  },
  ICE_HOCKEY: {en: 'Ice Hockey', fr: 'Hockey sur glace', es: 'Hockey sobre hielo', de: 'Eishockey'},
  LACROSSE: {en: 'Lacrosse', fr: 'Crosse', es: 'Lacrosse', de: 'Lacrosse'},
  RUGBY: {en: 'Rugby', fr: 'Rugby', es: 'Rugby', de: 'Rugby'},
  SOCCER: {en: 'Soccer/Football', fr: 'Football', es: 'Fútbol', de: 'Fußball'},
  SOFTBALL: {en: 'Softball', fr: 'Softball', es: 'Softbol', de: 'Softball'},
  ULTIMATE_DISC: {
    en: 'Ultimate Disc',
    fr: 'Ultimate',
    es: 'Disco Ultimate',
    de: 'Ultimate Frisbee',
  },
  VOLLEYBALL: {en: 'Volleyball', fr: 'Volley-ball', es: 'Voleibol', de: 'Volleyball'},
  RACKET_SPORTS: {
    en: 'Racket Sports',
    fr: 'Sports de raquette',
    es: 'Deportes de raqueta',
    de: 'Schlägersport',
  },
  BADMINTON: {en: 'Badminton', fr: 'Badminton', es: 'Bádminton', de: 'Badminton'},
  PADEL: {en: 'Padel', fr: 'Padel', es: 'Pádel', de: 'Padel'},
  PICKLEBALL: {en: 'Pickleball', fr: 'Pickleball', es: 'Pickleball', de: 'Pickleball'},
  PLATFORM_TENNIS: {
    en: 'Platform Tennis',
    fr: 'Tennis sur plateforme',
    es: 'Tenis de plataforma',
    de: 'Platform Tennis',
  },
  RACQUETBALL: {en: 'Racquetball', fr: 'Racquetball', es: 'Racquetball', de: 'Racquetball'},
  SQUASH: {en: 'Squash', fr: 'Squash', es: 'Squash', de: 'Squash'},
  TABLE_TENNIS: {en: 'Table Tennis', fr: 'Tennis de table', es: 'Tenis de mesa', de: 'Tischtennis'},
  TENNIS: {en: 'Tennis', fr: 'Tennis', es: 'Tenis', de: 'Tennis'},
  PARA_SPORTS: {
    en: 'Parasports',
    fr: 'Parasports',
    es: 'Deportes para personas con discapacidad',
    de: 'Parasport',
  },
  WHEELCHAIR_PUSH_RUN: {
    en: 'Pushing - Run Speed',
    fr: 'Poussée - Vitesse de course',
    es: 'Empujar - Velocidad de carrera',
    de: 'Anschieben – Lauftempo',
  },
  WHEELCHAIR_PUSH_WALK: {
    en: 'Pushing - Walk Speed',
    fr: 'Poussée - Vitesse de marche',
    es: 'Empujar - Velocidad de caminata',
    de: 'Anschieben – Gehtempo',
  },
  OTHER: {en: 'Other', fr: 'Autre', es: 'Otro', de: 'Sonstiges'},
  BOXING: {en: 'Boxing', fr: 'Boxe', es: 'Boxeo', de: 'Boxen'},
  BREATHWORK: {
    en: 'Breathwork',
    fr: 'Exercice de respiration',
    es: 'Respiración',
    de: 'Atemübungen',
  },
  DISC_GOLF: {en: 'Disc Golf', fr: 'Disc Golf', es: 'Golf de disco', de: 'Disc Golf'},
  FLOOR_CLIMBING: {
    en: 'Floor Climbing',
    fr: "Montée d'étages",
    es: 'Escalada de pisos',
    de: 'Stockwerksteigen',
  },
  GOLF: {en: 'Golf', fr: 'Golf', es: 'Golf', de: 'Golf'},
  INLINE_SKATING: {en: 'Inline Skating', fr: 'Roller', es: 'Patinaje en línea', de: 'Inlineskaten'},
  MIXED_MARTIAL_ARTS: {
    en: 'Mixed Martial Arts',
    fr: 'Arts martiaux mixtes',
    es: 'Artes marciales mixtas',
    de: 'Mixed Martial Arts',
  },
  MOUNTAINEERING: {en: 'Mountaineering', fr: 'Alpinisme', es: 'Montañismo', de: 'Bergsteigen'},
  ROCK_CLIMBING: {en: 'Rock Climbing', fr: 'Escalade', es: 'Escalada en roca', de: 'Klettern'},
  STOP_WATCH: {en: 'Stopwatch', fr: 'Chronomètre', es: 'Cronómetro', de: 'Stoppuhr'},

  // NOT IN DOCS
  DIVING: {en: 'Diving', fr: 'Plongée', es: 'Buceo', de: 'Tauchen'},
  APNEA: {en: 'Apnea', fr: 'Apnée', es: 'Apnea', de: 'Apnoetauchen'},
  APNEA_HUNTING: {en: 'Apnea Hunt', fr: 'Chasse en apnée', es: 'Caza de apnea', de: 'Apnoe-Jagd'},
  CGR_DIVING: {en: 'CCR Dive', fr: 'Plongée avec RCF', es: 'Buceo CCR', de: 'CCR-Tauchen'},
  GAUGE_DIVING: {
    en: 'Gauge Dive',
    fr: 'Plongée avec profondimètre',
    es: 'Buceo con medidor de profundidad',
    de: 'Tauchen mit Tiefenmesser',
  },
  MULTI_GAS_DIVING: {
    en: 'Multi-Gas Dive',
    fr: 'Plongée multigaz',
    es: 'Buceo con múltiples gases',
    de: 'Multigas-Tauchen',
  },
  SINGLE_GAS_DIVING: {
    en: 'Single-Gas Dive',
    fr: 'Plongée monogaz',
    es: 'Buceo con un solo gas',
    de: 'Eingas-Tauchen',
  },
  CANOEING: {en: 'Canoeing', fr: 'Canoë', es: 'Canoísmo', de: 'Kanufahren'},
  WAKESURFING: {en: 'Wakesurfing', fr: 'Wakesurf', es: 'Wakesurf', de: 'Wakesurfen'},
  TUBING: {en: 'Tubing', fr: 'Bouée gonflable', es: 'Tubing', de: 'Tubing'},
  ARCHERY: {en: 'Archery', fr: "Tir à l'arc", es: 'Tiro con arco', de: 'Bogenschießen'},
  ATV: {en: 'ATV', fr: 'Quad', es: 'ATV', de: 'Quad'},
  AUTO_RACING: {en: 'Auto Racing', fr: 'Course auto', es: 'Carreras de autos', de: 'Autorennen'},
  DRIVING: {en: 'Driving', fr: 'Conduite', es: 'Conducción', de: 'Autofahren'},
  GAMING: {en: 'Gaming', fr: 'Jeux', es: 'Videojuegos', de: 'Gaming'},
  FLYING: {en: 'Flying', fr: 'Aviation', es: 'Vuelo', de: 'Fliegen'},
  HAND_GLIDING: {
    en: 'Hang Gliding',
    fr: 'Deltaplane',
    es: 'Vuelo en ala delta',
    de: 'Drachenfliegen',
  },
  HORSEBACK_RIDING: {en: 'Horseback Riding', fr: 'Equitation', es: 'Equitación', de: 'Reiten'},
  HUNTING: {en: 'Hunting', fr: 'Chasse', es: 'Caza', de: 'Jagen'},
  MEDITATING: {en: 'Meditation', fr: 'Méditation', es: 'Meditación', de: 'Meditation'},
  MOTOCROSS: {en: 'Motocross', fr: 'Motocross', es: 'Motocross', de: 'Motocross'},
  MOTORCYCLING: {en: 'Motorcycling', fr: 'Moto-cross', es: 'Motociclismo', de: 'Motorradfahren'},
  OVERLAND: {en: 'Overland', fr: 'Overlanding', es: 'Viaje por tierra', de: 'Overlanding'},
  DRONE: {en: 'RC/Drone', fr: 'Télécommande Radio du Drone', es: 'RC/Drone', de: 'RC/Drohne'},
  SKY_DIVING: {en: 'Sky Diving', fr: 'Parachutisme', es: 'Paracaidismo', de: 'Fallschirmspringen'},
  WINGSUIT_FLYING: {
    en: 'Wingsuit Flying',
    fr: 'Vol en wingsuit',
    es: 'Vuelo en traje de alas',
    de: 'Wingsuit-Fliegen',
  },
  // 191: {en: 'Bike to Run Transition', fr: 'Transition entre vélo et course'},
  // 192: {en: 'Run to Bike Transition', fr: 'Transition entre course et vélo'},
  // 190: {en: 'Swim to Bike Transition', fr: 'Transition entre natation et vélo'},
  // 189: {en: 'Transition', fr: 'Transition'},
};

export function getGarminConnectActivityGroupLabel(
  activityGroup: GarminConnectTriggerIntegrationActivityGroupsType,
  language: Language
): string {
  return (
    GarminConnectTriggerIntegrationActivityGroupsTranslation[activityGroup][language] ??
    GarminConnectTriggerIntegrationActivityGroupsTranslation[activityGroup].en ??
    activityGroup
  );
}

export function getGarminConnectActivityTypeLabel(
  activityType: GarminConnectTriggerIntegrationActivityTypes,
  language: Language
): string {
  return (
    GarminConnectTriggerIntegrationActivityTypesTranslation[activityType][language] ??
    GarminConnectTriggerIntegrationActivityTypesTranslation[activityType].en ??
    activityType
  );
}

export const GarminConnectActivitySchema = z.object({
  userId: z.string(),
  summaryId: z.string(),
  activityId: z.number(),
  activityName: z.string(),
  durationInSeconds: z.number().optional(),
  startTimeInSeconds: z.number(),
  averageSpeedInMetersPerSecond: z.number().optional(),
  distanceInMeters: z.number().optional(),
  totalElevationGainInMeters: z.number().optional(),
  activityType: GarminConnectTriggerIntegrationAvailableActivityTypesSchema,
});

export type GarminConnectActivity = z.infer<typeof GarminConnectActivitySchema>;
